/** @jsxImportSource @emotion/react */

import { cloneElement, useCallback } from "react";
import { Flex } from "../utilities";
import { getValidChildren } from "../utils";
import { useTabContext } from "./Tab.provider";
import { tabListCSS } from "./Tabs.css";
import { TabsIndicator } from "./Tabs.indicator";
import type { TabListProps } from "./Tabs.types";

export const TabList = ({ children, className, sx }: TabListProps) => {
  const tabContext = useTabContext();
  const tabKeyList = getValidChildren(children).map((_, index) => index);
  const len = tabKeyList.length;

  const renderTabs = () =>
    getValidChildren(children).map((child, index) => {
      return cloneElement(child, {
        // eslint-disable-next-line react/no-array-index-key
        key: index,
        _onClick: () => {
          tabContext.setActiveIndex(index);
        },
        _index: index,
      });
    });

  const keyDownHandler = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const { activeIndex, setActiveIndex } = tabContext;

      const jump = (index: number) => {
        setActiveIndex(index);
      };

      const move = (step: number) => {
        jump(tabKeyList[(((activeIndex + step) % len) + len) % len]);
      };

      switch (event.key) {
        case "ArrowLeft":
          move(-1);
          break;
        case "ArrowRight":
          move(1);
          break;
        case "Home":
          jump(tabKeyList[0]);
          break;
        case "End":
          jump(tabKeyList[tabKeyList.length - 1]);
          break;

        default:
          break;
      }
    },
    [len, tabContext, tabKeyList],
  );

  return (
    <Flex
      className={className}
      sx={sx}
      position="relative"
      display={tabContext.fullWidth ? "flex" : "inline-flex"}
      width={tabContext.fullWidth ? "100%" : "fit-content"}
      flexDirection={tabContext.direction === "vertical" ? "column" : "row"}
      native={{
        role: "tablist",
        onKeyDown: keyDownHandler,
        "aria-orientation": tabContext.direction,
      }}
      whiteSpace="nowrap"
      css={[
        tabListCSS({
          variant: tabContext.variant,
          size: tabContext.size,
        }),
        {
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
      ]}
    >
      {renderTabs()}

      {tabContext.variant === "text" && tabContext.underline && <TabsIndicator />}
    </Flex>
  );
};
